import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { fetchData } from '../../../helpers/methods';

import CommentInput from './sub-components/comment-input';
import Comment from './sub-components/comment';

import SpinnerContainer from '../../../component-library/spinner-container';

const Comments = ({
	id,
}) => {
	const [loading, setLoading] = useState(true);
	const [conversationId, setConversationId] = useState('');
	const [allCommentsData, setAllCommentsData] = useState([]);
	const allCommentsDataRef = useRef();
	allCommentsDataRef.current = allCommentsData;
	const [newComment, setNewComment] = useState('');
	const [newCommentError, setNewCommentError] = useState('');
	const [totalResults, setTotalResults] = useState(0);
	const totalResultsRef = useRef();
	totalResultsRef.current = totalResults;
	const [pageOffset, setPageOffset] = useState(0);
	const pageOffsetRef = useRef();
	pageOffsetRef.current = pageOffset;
	const pageSize = 20;
	const maxCommentLength = 512;

	const getAllCommentsData = () => {
		fetchData({
			uri: `/api/web/comments/catitem/${id}?pageSize=${pageSize}`,
			method: 'GET',
		})
			.then(res => {
				const newAllCommentsData = res.body.conversations[0].comments;
				const newConversationId = res.body.conversations[0].conversation.key;
				const newTotalResults = res.body.conversations[0].conversation.last;
				setTotalResults(newTotalResults);
				setPageOffset(0);
				setConversationId(newConversationId);
				setAllCommentsData(newAllCommentsData);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const getAllCommentsDataPaginated = newOffset => {
		fetchData({
			uri: `/api/web/comments/catitem/${id}?pageSize=${pageSize}&offset=${newOffset}`,
			method: 'GET',
		})
			.then(res => {
				const newComments = res.body.conversations[0].comments;
				const newTotalResults = res.body.conversations[0].conversation.last;
				let newAllCommentsData = allCommentsDataRef.current;
				newAllCommentsData = [...newAllCommentsData, ...newComments];
				setTotalResults(newTotalResults);
				setAllCommentsData(newAllCommentsData);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const handleAddComment = () => {
		const commentData = {
			message: newComment,
		};
		fetchData({
			uri: `/api/web/comments/catitem/${id}/conv/${conversationId}`,
			method: 'POST',
			options: {
				body: JSON.stringify(commentData),
			},
		})
			.then(({ ok }) => {
				if (ok) {
					setLoading(true);
					setNewComment('');
					setNewCommentError('');
					getAllCommentsData();
				} else {
					setNewCommentError('Sorry, your comment could not be posted at this time, please try again.');
				}
			})
			.catch(() => {
				setNewCommentError('Sorry, your comment could not be posted at this time, please try again.');
			});
	};

	const handleAddCommentValidation = () => {
		setNewCommentError('');
		if (newComment !== '') {
			if (newComment.length > maxCommentLength) {
				setNewCommentError(`Comments must be less than ${maxCommentLength} characters in length.`);
			} else {
				handleAddComment();
			}
		} else {
			setNewCommentError('You must enter a comment.');
		}
	};

	const handleScroll = () => {
		const container = document.getElementById('mainContainer');
		if (window.innerHeight + window.pageYOffset >= container.offsetHeight + 61) {
			const newPageOffset = pageOffsetRef.current + pageSize;
			if (newPageOffset <= totalResultsRef.current) {
				setPageOffset(newPageOffset);
				getAllCommentsDataPaginated(newPageOffset);
			}
		}
	};

	useEffect(() => {
		getAllCommentsData();
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	return (
		<div>
			<div className="flex-row justify-space-between align-items-center border-bottom margin-bottom-16">
				<h3>Comments</h3>
			</div>
			{loading && (
				<SpinnerContainer padding={false} />
			)}
			{!loading && (
				<div className="comments margin-bottom-24">
					<CommentInput
						placeholder="Add a comment..."
						formAction={value => setNewComment(value)}
						formValue={newComment}
						formError={newCommentError}
						buttonTitle="Post Comment"
						buttonAction={() => handleAddCommentValidation()}
						maxCommentLength={maxCommentLength}
					/>
					{allCommentsData?.length ? (
						allCommentsData.map((comment, index) => (
							<Comment
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								id={id}
								comment={comment}
								canReply
								maxCommentLength={maxCommentLength}
							/>
						))
					) : (
						<p className="grey-text">No comments found</p>
					)}
				</div>
			)}
		</div>
	);
};

export default Comments;

Comments.propTypes = {
	id: PropTypes.string.isRequired,
};
